import { Link } from 'react-router-dom';
import { Crown, Play, Users, Star, Zap, Shield, Sword } from 'lucide-react';
import { Button } from '../components/ui/button';
import GlassCard from '../components/GlassCard';
import Starfield from '../components/Starfield';
import LiveStats from '../components/LiveStats';
import StatCard from '../components/StatCard';

const LandingPage = () => {
  const features = [
    {
      icon: Star,
      title: 'Galaxy Conquest',
      description: 'Conquer star systems and expand your empire across the cosmos',
      variant: 'premium' as const
    },
    {
      icon: Users,
      title: 'Alliance Warfare',
      description: 'Form strategic alliances and engage in massive fleet battles',
      variant: 'epic' as const
    },
    {
      icon: Zap,
      title: 'Real-time Strategy',
      description: 'Make tactical decisions that shape the fate of entire civilizations',
      variant: 'legendary' as const
    }
  ];

  const gameStats = [
    { title: 'Active Players', value: '50K+', trend: 'up' as const, icon: Users },
    { title: 'Battles Today', value: '10K+', trend: 'up' as const, icon: Sword },
    { title: 'Alliances', value: '1K+', trend: 'neutral' as const, icon: Shield },
    { title: 'Systems Conquered', value: '100K+', trend: 'up' as const, icon: Star }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-space-void via-space-deep to-space-purple relative overflow-hidden">
      <Starfield density={300} speed={0.8} />
      
      <div className="relative z-10">
        {/* Header */}
        <header className="container mx-auto px-4 py-6">
          <nav className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-accent-gold animate-glow" />
              <span className="text-2xl font-bold text-white">Crown of Suns</span>
            </div>
            
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost" className="text-white hover:bg-glass-hover">
                  Sign In
                </Button>
              </Link>
              <Link to="/register">
                <Button className="bg-gradient-to-r from-space-star to-accent-violet hover:from-space-star/80 hover:to-accent-violet/80">
                  Join Empire
                </Button>
              </Link>
            </div>
          </nav>
        </header>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-20 text-center">
          <div className="max-w-4xl mx-auto">
            <h1 className="text-6xl md:text-7xl font-bold text-white mb-6 leading-tight">
              Rule the{' '}
              <span className="bg-gradient-to-r from-accent-gold via-space-star to-accent-violet bg-clip-text text-transparent animate-glow">
                Galaxy
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-8 max-w-2xl mx-auto">
              Command fleets, forge alliances, and conquer star systems in the ultimate space strategy game
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link to="/register">
                <Button size="lg" className="bg-gradient-to-r from-accent-gold to-accent-red hover:from-accent-gold/80 hover:to-accent-red/80 text-white font-bold text-lg px-8 py-4 animate-float">
                  <Play className="w-5 h-5 mr-2" />
                  Start Playing Free
                </Button>
              </Link>
              
              <Button variant="outline" size="lg" className="border-glass-border bg-glass-bg hover:bg-glass-hover text-white">
                Watch Trailer
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Epic Space Warfare
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Experience the ultimate in strategic gameplay with cutting-edge features
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <GlassCard 
                key={index} 
                variant={feature.variant} 
                glow={true} 
                animated={true}
                className="p-8 text-center"
              >
                <feature.icon className="w-12 h-12 mx-auto mb-4 text-accent-gold" />
                <h3 className="text-xl font-bold text-white mb-3">{feature.title}</h3>
                <p className="text-white/70">{feature.description}</p>
              </GlassCard>
            ))}
          </div>
        </section>

        {/* Stats Section */}
        <section className="container mx-auto px-4 py-20">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Join the Battle
            </h2>
            <p className="text-xl text-white/70">
              Thousands of commanders are already conquering the galaxy
            </p>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {gameStats.map((stat, index) => (
              <StatCard
                key={index}
                title={stat.title}
                value={stat.value}
                trend={stat.trend}
                icon={<stat.icon className="w-8 h-8" />}
                variant={index % 2 === 0 ? 'premium' : 'epic'}
                animated={true}
              />
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-20 text-center">
          <GlassCard variant="legendary" glow={true} className="max-w-4xl mx-auto p-12">
            <Crown className="w-16 h-16 mx-auto mb-6 text-accent-gold animate-glow" />
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Your Empire Awaits
            </h2>
            <p className="text-xl text-white/80 mb-8 max-w-2xl mx-auto">
              Join millions of players in the most epic space strategy game ever created. 
              Build your fleet, forge alliances, and rule the galaxy.
            </p>
            
            <Link to="/register">
              <Button size="lg" className="bg-gradient-to-r from-accent-gold to-accent-red hover:from-accent-gold/80 hover:to-accent-red/80 text-white font-bold text-xl px-12 py-4">
                Create Your Empire Now
              </Button>
            </Link>
          </GlassCard>
        </section>
      </div>
      
      {/* Live Stats Footer - Matching Figma Design */}
      <div className="relative z-20">
        <LiveStats className="mx-auto max-w-6xl h-32" />
      </div>
    </div>

  )
}

export default LandingPage